import React, { useCallback, useRef } from 'react';
import { Block, ScopeListBlockData, ScopeListItem, generateBlockId } from '../../../config';
import LiveText from '../../LiveText';
import { readSubscripts } from '../../../subscript';
import './scope-list-block.scss';

interface ScopeListBlockProps {
  block: Block;
  onChange: (data: ScopeListBlockData) => void;
  onDelete: () => void;
  isEditing: boolean;
}

const ScopeListBlock: React.FC<ScopeListBlockProps> = ({ block, onChange, isEditing }) => {
  const data = block.data as ScopeListBlockData;
  const listRef = useRef<HTMLUListElement>(null);
  
  // Default to showing checkboxes for to-do lists
  const showCheckboxes = data.showCheckboxes !== false;

  const updateItem = useCallback((index: number, updates: Partial<ScopeListItem>) => {
    const newItems = [...data.items];
    newItems[index] = { ...newItems[index], ...updates };
    onChange({ ...data, items: newItems });
  }, [data, onChange]);

  const toggleComplete = useCallback((index: number) => {
    const newItems = [...data.items];
    newItems[index] = { ...newItems[index], completed: !newItems[index].completed };
    onChange({ ...data, items: newItems });
  }, [data, onChange]);

  const deleteItem = useCallback((index: number) => {
    if (data.items.length <= 1) return;
    const newItems = data.items.filter((_, i) => i !== index);
    onChange({ ...data, items: newItems });
    
    // Focus the previous item after deletion
    setTimeout(() => {
      const items = listRef.current?.querySelectorAll('.item-text');
      const prevIndex = Math.max(0, index - 1);
      const prevItem = items?.[prevIndex] as HTMLElement;
      if (prevItem) {
        prevItem.focus();
        const range = document.createRange();
        const sel = window.getSelection();
        range.selectNodeContents(prevItem);
        range.collapse(false);
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }, 0);
  }, [data, onChange]);

  const indentItem = useCallback((index: number, direction: 1 | -1) => {
    const currentIndent = data.items[index].indent;
    const newIndent = Math.max(0, Math.min(3, currentIndent + direction));
    updateItem(index, { indent: newIndent });
  }, [data.items, updateItem]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // One update for both the edited text and the inserted item; two calls
      // built from the same stale `data.items` would overwrite each other.
      // As LiveText reads it: a subscript stays one (subscript.ts).
      const currentText = readSubscripts(e.target as HTMLElement);
      const newItem: ScopeListItem = {
        id: generateBlockId(),
        text: '',
        indent: data.items[index]?.indent || 0
      };
      const newItems = [...data.items];
      newItems[index] = { ...newItems[index], text: currentText };
      newItems.splice(index + 1, 0, newItem);
      onChange({ ...data, items: newItems });

      setTimeout(() => {
        const items = listRef.current?.querySelectorAll('.item-text');
        const newItemEl = items?.[index + 1] as HTMLElement;
        newItemEl?.focus();
      }, 0);
    } else if (e.key === 'Backspace') {
      const currentText = (e.target as HTMLElement).textContent || '';
      if (!currentText.trim()) {
        e.preventDefault();
        deleteItem(index);
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      indentItem(index, e.shiftKey ? -1 : 1);
    }
  }, [data, onChange, deleteItem, indentItem]);

  return (
    <div className="scope-list-block">
      <ul ref={listRef} className="scope-list">
        {data.items.map((item, index) => (
          <li 
            key={item.id} 
            className={`scope-item indent-${item.indent} ${showCheckboxes ? 'has-checkbox' : ''}`}
          >
            {showCheckboxes && (
              <button 
                className={`checkbox ${item.completed ? 'checked' : ''}`}
                onClick={() => toggleComplete(index)}
                disabled={!isEditing}
                type="button"
                aria-label={item.completed ? 'Mark as incomplete' : 'Mark as complete'}
              >
                {item.completed && (
                  <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path 
                      d="M11.5 3.5L5.5 10.5L2.5 7.5" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            )}
            <LiveText
              className={`item-text ${item.completed ? 'completed' : ''}`}
              value={item.text}
              editable={isEditing}
              restoreKey={`${block.id}:${item.id}`}
              onCommit={(text) => updateItem(index, { text })}
              onKeyDown={(e) => handleKeyDown(e, index)}
              data-placeholder="To-do"
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ScopeListBlock;
